/**
 * JWT Context Service - Backend-based JWT validation and extraction
 *
 * This service replaces client-side JWT decoding with secure server-side
 * validation and extraction. Instead of decoding the JWT token on the
 * client (which doesn't validate signature), we send it to the backend
 * for proper RS256 signature validation and context extraction.
 *
 * Security Benefits:
 * - JWT signature validated server-side (tamper-proof)
 * - Expired tokens rejected by backend (401 Unauthorized)
 * - Single source of truth (backend validates, frontend trusts)
 * - No client-side crypto dependencies
 *
 * Flow:
 * 1. Parent app sends JWT token via PostMessage
 * 2. Frontend calls POST /api/v1/jwt/extract-context with token
 * 3. Backend validates JWT signature (Spring Security RS256)
 * 4. Backend extracts all claims from JWT payload
 * 5. Backend returns validated context data
 * 6. Frontend uses validated context (no client-side decoding needed)
 *
 * @module jwtContextService
 */

import axiosInstance from './api/axiosInstance';
import { logger } from '../utils/logger';

/**
 * JWT context response matching backend JwtContextResponse DTO
 *
 * IMPORTANT: Contains ONLY confidential data extracted from JWT.
 * Application metadata (clientId, tenantId, language)
 * is passed separately in the parent context object, not from JWT.
 *
 * Frontend should merge this with context metadata:
 * ```typescript
 * const fullContext = {
 *   // Application metadata (from parent context)
 *   clientId: context.clientId,
 *   tenantId: context.tenantId,
 *   language: context.language,
 *   token: jwtToken,
 *   // Confidential data (from JWT, validated by backend)
 *   ...jwtResponse
 * };
 * ```
 */
export interface JwtContextResponse {
  clientInfo?: ClientInfo;
  contracts?: Contract[];
  bankingInfo?: BankingInfo;
  issuedAt?: number;
  expiresAt?: number;
  cacheTimestamp: number;
}

export interface ClientInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  provinceOrState?: string;
  language?: 'FR' | 'EN';
  dateOfBirth?: string;
  gender?: 'M' | 'F' | 'X';
  healthCard?: string;
  hasHealthDeclaration?: boolean;
  address?: Address;
}

export interface Address {
  streetAddress?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
}

export interface Contract {
  contractNumber: string;
  groupNumber?: string;
  type?: string;
  validFrom?: string;
  validTo?: string;
  insurer?: string;
  benefits?: string[];
  coverage?: string[];
  isActive?: boolean;
  trips?: Trip[];
  beneficiaries?: Beneficiary[];
}

export interface Trip {
  tripId?: string;
  destination?: string;
  departureDate?: string;
  returnDate?: string;
  purpose?: string;
  status?: string;
}

export interface Beneficiary {
  id?: string;
  firstName?: string;
  lastName?: string;
  relationship?: string;
  dateOfBirth?: string;
  healthCard?: string;
  provinceOrState?: string;
  province?: string;
  gender?: 'M' | 'F' | 'X';
  hasHealthDeclaration?: boolean;
}

export interface BankingInfo {
  bankName?: string;
  routingNumber?: string;
  accountNumber?: string;
  accountHolderName?: string;
  accountType?: 'checking' | 'savings';
}

/**
 * Extract and validate JWT context from backend.
 *
 * This function sends the JWT token to the backend for signature validation
 * and context extraction. The backend will:
 * 1. Validate JWT signature using RS256 algorithm
 * 2. Verify JWT expiration date
 * 3. Extract all claims (tenantId, clientInfo, contracts, etc.)
 * 4. Return structured context data
 *
 * Error Handling:
 * - 401 Unauthorized: JWT signature invalid or token expired
 * - 400 Bad Request: Missing or malformed token or tenant ID
 * - 500 Internal Server Error: Backend processing error
 *
 * Example Usage:
 * ```typescript
 * try {
 *   const context = await extractJwtContext(jwtToken, 'BLUE_CROSS_INSURANCE');
 *   console.log('Tenant ID:', context.tenantId);
 *   console.log('Client:', context.clientInfo);
 *   console.log('Contracts:', context.contracts);
 * } catch (error) {
 *   console.error('JWT validation failed:', error);
 *   // Token is invalid or expired
 * }
 * ```
 *
 * @param token - JWT token string (header.payload.signature)
 * @param tenantId - Tenant ID for multi-tenant isolation (REQUIRED)
 * @returns Promise<JwtContextResponse> - Validated and extracted context
 * @throws Error if JWT validation fails (invalid signature, expired, etc.)
 */
export async function extractJwtContext(token: string, tenantId?: string): Promise<JwtContextResponse> {
  try {
    // Call backend endpoint for JWT validation and extraction
    // IMPORTANT: Pass JWT directly in Authorization header AND X-Tenant-ID header (bypass interceptor logic)
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`
    };

    // CRITICAL: Inject X-Tenant-ID header if provided (required for multi-tenant backend)
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }

    const response = await axiosInstance.post<{ payload: JwtContextResponse }>(
      '/api/v1/jwt/extract-context',
      { token },
      { headers }
    );

    const context = response.data.payload;

    return context;

  } catch (error: any) {
    // Log error details
    logger.error('JWT validation failed', {
      errorType: 'JwtValidationError',
      service: 'JwtContextService',
      httpStatus: error.response?.status
    }, error);

    // Re-throw with more context
    if (error.response?.status === 401) {
      throw new Error('JWT token is invalid or expired. Please obtain a new token.');
    } else if (error.response?.status === 400) {
      throw new Error('Invalid JWT token format.');
    } else {
      throw new Error('Failed to validate JWT token: ' + (error.message || 'Unknown error'));
    }
  }
}

/**
 * Check if a string looks like a JWT token.
 *
 * JWT format: header.payload.signature (3 parts separated by dots)
 * Each part is Base64URL encoded.
 *
 * Note: This is a simple format check, NOT validation.
 * Actual validation (signature, expiration) happens server-side.
 *
 * @param token - String to check
 * @returns true if string appears to be a JWT
 */
export function isJWT(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  // Check if each part is valid Base64URL
  const base64UrlRegex = /^[A-Za-z0-9_-]+$/;
  return parts.every(part => part.length > 0 && base64UrlRegex.test(part));
}
